import React, { useCallback, useRef, useState } from "react";
import { graphql } from "react-relay";

import { faMicrophone, faPause, faPlay, faStop } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { useFeatureIsOn } from "@growthbook/growthbook-react";

import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { uploadFileToURL } from "~/recordings/uploads/UploadModal";
import { toast } from "~/ui";
import Menu from "~/ui/menu";

import { WEB_BOTLESS_RECORDING_FEATURE_FLAG } from "../featureFlag";

import type { MicrophoneRecording_completeClientSideRecordingMutation as CompleteClientSideRecordingMutation } from "~/__generated__/MicrophoneRecording_completeClientSideRecordingMutation.graphql";
import type { MicrophoneRecording_createClientSideRecordingMutation as CreateClientSideRecordingMutation } from "~/__generated__/MicrophoneRecording_createClientSideRecordingMutation.graphql";
import type { MicrophoneRecording_getSignedUploadMutation as GetSignedUploadMutation } from "~/__generated__/MicrophoneRecording_getSignedUploadMutation.graphql";

type RecordingState = "inactive" | "recording" | "paused";

interface UseMediaRecorderResult {
	startRecording: () => Promise<void>;
	pauseRecording: () => void;
	resumeRecording: () => void;
	stopRecording: () => Promise<[File, readonly Pause[]]>;
	state: RecordingState;
}

interface Pause {
	startTime: number; // seconds into the recording when pause started
	duration: number; // duration in seconds, populated when recording is resumed
}

const candidateMimeTypes = [
	"audio/mp4; codecs=mp4a.40.2",
	"audio/mp4;codecs=aac",
	"audio/x-m4a",
	"audio/webm;codecs=opus",
	"audio/webm",
];

export function useMediaRecorder(): UseMediaRecorderResult {
	const [, _forceRerender] = useState(0);
	const forceRerender = useCallback(() => _forceRerender(prev => prev + 1), []);

	const recordingSession = useRef<{
		recorder: MediaRecorder;
		dataChunks: Blob[];
		pauses: Pause[];
		recordingStartTime: number;
		totalPauseDuration: number;
	} | null>(null);

	const startRecording = useCallback(async () => {
		let stream: MediaStream;
		try {
			stream = await navigator.mediaDevices.getUserMedia({ audio: true });
		} catch (error) {
			if (error instanceof DOMException && error.name === "NotAllowedError") {
				toast.error(
					<>
						Fellow needs microphone permission in order to record. Please go to your browser’s settings to grant
						microphone permission to Fellow.
						{/* TODO: Once a help center article is written, add a link to it here. */}
						{/* <HelpCenterLink articleId="TKTK">
							Learn more.
						</HelpCenterLink> */}
					</>,
				);
				return;
			} else {
				toast.error("An unknown error occurred while trying to access your microphone.");
				throw error;
			}
		}

		const mimeType = candidateMimeTypes.find(type => MediaRecorder.isTypeSupported(type));
		if (!mimeType) {
			throw new Error("No supported recording type found");
		}

		if (recordingSession.current) {
			throw new Error("Already recording");
		}
		recordingSession.current = {
			recorder: new MediaRecorder(stream, {
				mimeType,
				audioBitsPerSecond: 92000,
			}),
			dataChunks: [],
			pauses: [],
			recordingStartTime: Date.now(),
			totalPauseDuration: 0,
		};

		const { recorder, dataChunks } = recordingSession.current;

		recorder.addEventListener("dataavailable", e => {
			if (e.data.size > 0) {
				dataChunks.push(e.data);
			}
		});

		recorder.start();
		forceRerender();
	}, [forceRerender]);

	const getRecordingElapsedTime = useCallback(() => {
		if (!recordingSession.current) return 0;
		const { recordingStartTime, totalPauseDuration } = recordingSession.current;
		// Calculate seconds elapsed since starting, minus paused time
		return (Date.now() - recordingStartTime - totalPauseDuration) / 1000;
	}, []);

	const pauseRecording = useCallback(() => {
		if (!recordingSession.current) {
			throw new Error("Can't pause, not recording");
		}
		if (recordingSession.current.recorder.state === "recording") {
			recordingSession.current.recorder.pause();
			// Record when the pause started (in seconds into the recording)
			recordingSession.current.pauses.push({
				startTime: getRecordingElapsedTime(),
				duration: Infinity,
			});
			forceRerender();
		}
	}, [forceRerender, getRecordingElapsedTime]);

	const resumeRecording = useCallback(() => {
		if (!recordingSession.current) {
			throw new Error("Can't resume, no current recording");
		}
		if (recordingSession.current.recorder.state === "paused") {
			// Calculate the duration of the pause
			const pauses = recordingSession.current.pauses;
			if (pauses.length > 0) {
				const lastPause = pauses[pauses.length - 1];
				if (lastPause.duration === Infinity) {
					// Convert ms to seconds for the duration
					const pauseDurationMs =
						Date.now() -
						(recordingSession.current.recordingStartTime +
							lastPause.startTime * 1000 +
							recordingSession.current.totalPauseDuration);
					lastPause.duration = pauseDurationMs / 1000;
					recordingSession.current.totalPauseDuration += pauseDurationMs;
				}
			}

			recordingSession.current.recorder.resume();
			forceRerender();
		}
	}, [forceRerender]);

	const stopRecording = useCallback(async () => {
		return new Promise<[File, readonly Pause[]]>((resolve, reject) => {
			if (!recordingSession.current) {
				throw new Error("No media recorder found");
			}
			const { recorder, dataChunks, pauses } = recordingSession.current;

			recorder.addEventListener("stop", () => {
				let extension = "webm";
				if (recorder.mimeType.startsWith("audio/mp4") || recorder.mimeType.startsWith("audio/x-m4a")) {
					extension = "m4a";
				}
				const file = new File(dataChunks, `recording.${extension}`, {
					type: recorder.mimeType,
				});

				// Clean up
				recorder.stream.getTracks().forEach(track => track.stop());
				recordingSession.current = null;

				resolve([file, pauses.filter(pause => pause.duration !== Infinity)]);
				forceRerender();
			});

			recorder.addEventListener("error", e => {
				if ("error" in e) {
					reject(e.error);
				} else {
					console.error(e);
					reject(new Error(String(e)));
				}
			});

			recorder.stop();
		});
	}, [forceRerender]);

	const getState = (): RecordingState => {
		if (!recordingSession.current) return "inactive";
		switch (recordingSession.current.recorder.state) {
			case "recording":
				return "recording";
			case "paused":
				return "paused";
			default:
				return "inactive";
		}
	};

	return {
		startRecording,
		pauseRecording,
		resumeRecording,
		stopRecording,
		state: getState(),
	};
}

export default function useIsMicrophoneRecordingEnabled() {
	return useFeatureIsOn(WEB_BOTLESS_RECORDING_FEATURE_FLAG);
}

export async function createClientSideRecording(noteId?: string) {
	const result = await commitMutationPromise<CreateClientSideRecordingMutation>(environment, {
		mutation: graphql`
			mutation MicrophoneRecording_createClientSideRecordingMutation($noteId: ID) {
				startMicrophoneRecording(noteId: $noteId) {
					meetingRecording {
						id
					}
				}
			}
		`,
		variables: { noteId },
	});
	if (!result.startMicrophoneRecording) {
		throw new Error("Failed to create client side recording");
	}
	return result.startMicrophoneRecording.meetingRecording;
}

export async function getSignedUploadForClientSideMeetingRecording(meetingRecordingId: string, fileExtension: string) {
	const result = await commitMutationPromise<GetSignedUploadMutation>(environment, {
		mutation: graphql`
			mutation MicrophoneRecording_getSignedUploadMutation($meetingRecordingId: ID!, $fileExtension: String!) {
				getSignedUploadForClientSideMeetingRecording(
					meetingRecordingId: $meetingRecordingId
					fileExtension: $fileExtension
				) {
					uploadUrl
					fields
				}
			}
		`,
		variables: { meetingRecordingId, fileExtension },
	});
	if (!result.getSignedUploadForClientSideMeetingRecording) {
		throw new Error("Failed to get signed upload for client side meeting recording");
	}
	return [
		result.getSignedUploadForClientSideMeetingRecording.uploadUrl,
		result.getSignedUploadForClientSideMeetingRecording.fields,
	];
}

export async function testCreateAndUploadClientSideRecording(file: File, noteId?: string) {
	if (window.ENVIRONMENT !== "dev" && !window.ENVIRONMENT.startsWith("sandbox")) {
		throw new Error("This function is only available in development");
	}

	const meetingRecording = await createClientSideRecording(noteId);
	const [uploadUrl, uploadFields] = await getSignedUploadForClientSideMeetingRecording(
		meetingRecording.id,
		file.name.split(".").pop() ?? "m4a",
	);
	await uploadFileToURL(file, uploadUrl, uploadFields, progress => console.log(`Uploading… ${progress}%`));

	await completeClientSideMeetingRecording(meetingRecording.id, uploadFields.key, []);

	return meetingRecording.id;
}

export async function completeClientSideMeetingRecording(
	meetingRecordingId: string,
	audioFilePath: string,
	pauses: readonly Pause[],
) {
	const pausesArray = pauses.map(pause => [pause.startTime, pause.duration]);
	await commitMutationPromise<CompleteClientSideRecordingMutation>(environment, {
		mutation: graphql`
			mutation MicrophoneRecording_completeClientSideRecordingMutation(
				$meetingRecordingId: ID!
				$audioFilePath: String!
				$pauses: [[Float!]!]!
			) {
				completeClientSideMeetingRecording(
					meetingRecordingId: $meetingRecordingId
					audioFilePath: $audioFilePath
					pauses: $pauses
				) {
					meetingRecording {
						id
					}
				}
			}
		`,
		variables: { meetingRecordingId, audioFilePath, pauses: pausesArray },
	});
}

export interface MicrophoneRecordingProps {
	noteId: string;
	state: RecordingState;
	startRecording: () => Promise<void>;
	pauseRecording: () => void;
	resumeRecording: () => void;
	stopRecording: () => Promise<void>;
}

export function MicrophoneRecordingSubmenu({
	state,
	startRecording,
	pauseRecording,
	resumeRecording,
	stopRecording,
}: MicrophoneRecordingProps) {
	return (
		<Menu.Sub>
			<Menu.SubTrigger icon={faMicrophone}>Record with microphone</Menu.SubTrigger>
			<Menu.SubContent>
				{state === "inactive" ? (
					<Menu.Item icon={faPlay} onSelect={startRecording}>
						Start recording
					</Menu.Item>
				) : (
					<>
						{state === "paused" ? (
							<Menu.Item icon={faPlay} onSelect={resumeRecording}>
								Resume recording
							</Menu.Item>
						) : (
							<Menu.Item icon={faPause} onSelect={pauseRecording}>
								Pause recording
							</Menu.Item>
						)}
						<Menu.Item icon={faStop} onSelect={stopRecording}>
							Stop recording
						</Menu.Item>
					</>
				)}
			</Menu.SubContent>
		</Menu.Sub>
	);
}
